import { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

const API_BASE = '/admin/audit-logs';

const fmt = (iso) =>
  iso
    ? new Date(iso).toLocaleString('es-CO', {
        timeZone: 'America/Bogota',
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      })
    : '—';

const nameOf = (user) => {
  if (!user) return '—';
  const p = user.profile || {};
  return [p.firstName, p.lastName].filter(Boolean).join(' ') || user.email || '—';
};

const STATUS_CONFIG = {
  ALLOWED: { label: 'Permitido', color: 'bg-green-100 text-green-800' },
  DENIED:  { label: 'Denegado',  color: 'bg-red-100 text-red-800'    },
  ERROR:   { label: 'Error',     color: 'bg-yellow-100 text-yellow-800' },
};

const RESOURCE_LABELS = {
  MEDICAL_RECORD: 'Historia Clínica',
  PAYMENT:        'Pago',
  USER:           'Usuario',
  APPOINTMENT:    'Cita',
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status || '—', color: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

const EMPTY_FILTERS = { status: '', resource: '', dateFrom: '', dateTo: '' };

function FilterBar({ filters, onChange, onSearch, loading }) {
  return (
    <form
      className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
      onSubmit={(e) => { e.preventDefault(); onSearch(); }}
    >
      <select
        className="input-field"
        value={filters.status}
        onChange={(e) => onChange('status', e.target.value)}
      >
        <option value="">Todos los estados</option>
        {Object.entries(STATUS_CONFIG).map(([val, { label }]) => (
          <option key={val} value={val}>{label}</option>
        ))}
      </select>

      <select
        className="input-field"
        value={filters.resource}
        onChange={(e) => onChange('resource', e.target.value)}
      >
        <option value="">Todos los recursos</option>
        {Object.entries(RESOURCE_LABELS).map(([val, label]) => (
          <option key={val} value={val}>{label}</option>
        ))}
        <option value="MEDICAL_RECORD">Historia Clínica</option>
      </select>

      <div className="flex gap-2">
        <input
          type="date"
          className="input-field flex-1"
          value={filters.dateFrom}
          onChange={(e) => onChange('dateFrom', e.target.value)}
          title="Desde"
        />
        <input
          type="date"
          className="input-field flex-1"
          value={filters.dateTo}
          onChange={(e) => onChange('dateTo', e.target.value)}
          title="Hasta"
        />
      </div>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={loading}
          className="flex-1 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition disabled:opacity-50"
        >
          {loading ? 'Buscando…' : 'Buscar'}
        </button>
        <button
          type="button"
          onClick={() => { onChange('__reset'); onSearch(true); }}
          className="px-3 py-2 text-gray-500 hover:text-gray-800 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 transition"
          title="Limpiar"
        >
          ✕
        </button>
      </div>
    </form>
  );
}

function LogRow({ log, onExpand, expanded }) {
  return (
    <>
      <tr
        className="hover:bg-gray-50 transition cursor-pointer"
        onClick={() => onExpand(log._id)}
      >
        <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">{fmt(log.createdAt)}</td>
        <td className="px-4 py-3 text-sm font-medium text-gray-800">{nameOf(log.userId)}</td>
        <td className="px-4 py-3 text-xs text-gray-500">
          {RESOURCE_LABELS[log.resource] || log.resource}
        </td>
        <td className="px-4 py-3 text-xs font-mono text-gray-400 max-w-[120px] truncate">
          {log.resourceId || '—'}
        </td>
        <td className="px-4 py-3 text-xs text-gray-500">{log.action}</td>
        <td className="px-4 py-3"><StatusBadge status={log.status} /></td>
        <td className="px-4 py-3 text-xs text-gray-400">{log.ip || '—'}</td>
        <td className="px-4 py-3 text-center text-gray-400 text-xs">
          {expanded ? '▲' : '▼'}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-gray-50 border-b border-gray-100">
          <td colSpan={8} className="px-6 py-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
              <div>
                <p className="text-gray-400 font-semibold uppercase tracking-wide mb-1">Razón</p>
                <p className="text-gray-700">{log.reason || '—'}</p>
              </div>
              <div>
                <p className="text-gray-400 font-semibold uppercase tracking-wide mb-1">Método / Ruta</p>
                <p className="text-gray-700 font-mono">{log.method} {log.path}</p>
              </div>
              <div>
                <p className="text-gray-400 font-semibold uppercase tracking-wide mb-1">User Agent</p>
                <p className="text-gray-700 truncate">{log.userAgent || '—'}</p>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function AuditLogsPage() {
  const [logs,     setLogs]     = useState([]);
  const [total,    setTotal]    = useState(0);
  const [pages,    setPages]    = useState(1);
  const [page,     setPage]     = useState(1);
  const [filters,  setFilters]  = useState(EMPTY_FILTERS);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [expanded, setExpanded] = useState(null);

  const fetchLogs = useCallback(async (overrideFilters, overridePage = 1) => {
    setLoading(true);
    setError('');
    const f = overrideFilters ?? filters;
    try {
      const params = new URLSearchParams({ page: overridePage, limit: 50 });
      if (f.status)   params.set('status',   f.status);
      if (f.resource) params.set('resource', f.resource);
      if (f.dateFrom) params.set('dateFrom', f.dateFrom);
      if (f.dateTo)   params.set('dateTo',   f.dateTo);

      const { data } = await api.get(`${API_BASE}?${params}`);
      if (data.success) {
        setLogs(data.data);
        setTotal(data.total);
        setPages(data.pages);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error al cargar logs de auditoría');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchLogs(filters, page); }, [page]); // eslint-disable-line

  const handleSearch = (reset = false) => {
    const f = reset ? EMPTY_FILTERS : filters;
    if (reset) setFilters(EMPTY_FILTERS);
    setPage(1);
    setExpanded(null);
    fetchLogs(f, 1);
  };

  const handleFilterChange = (key, value) => {
    if (key === '__reset') { setFilters(EMPTY_FILTERS); return; }
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const toggleExpand = (id) => setExpanded((prev) => (prev === id ? null : id));

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Auditoría de Accesos</h1>
          <p className="text-gray-500 text-sm mt-1">
            {total} evento{total !== 1 ? 's' : ''} registrado{total !== 1 ? 's' : ''} · Haz clic en una fila para ver detalles
          </p>
        </div>

        <FilterBar
          filters={filters}
          onChange={handleFilterChange}
          onSearch={handleSearch}
          loading={loading}
        />

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-4 text-sm">
            {error}
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {loading && logs.length === 0 ? (
            <div className="text-center py-16 text-gray-400">Cargando…</div>
          ) : logs.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-4xl mb-3">🔍</p>
              <p className="text-gray-500">No se encontraron eventos de auditoría</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    {['Fecha', 'Usuario', 'Recurso', 'ID Recurso', 'Acción', 'Estado', 'IP', ''].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {logs.map((log) => (
                    <LogRow
                      key={log._id}
                      log={log}
                      onExpand={toggleExpand}
                      expanded={expanded === log._id}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {pages > 1 && (
          <div className="flex justify-center gap-2 mt-4">
            <button
              disabled={page === 1 || loading}
              onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition"
            >
              ← Anterior
            </button>
            <span className="px-3 py-1.5 text-sm text-gray-600">Página {page} de {pages}</span>
            <button
              disabled={page === pages || loading}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition"
            >
              Siguiente →
            </button>
          </div>
        )}
      </div>

      <style>{`
        .input-field {
          width: 100%;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          padding: 0.45rem 0.75rem;
          font-size: 0.875rem;
          outline: none;
          transition: border-color 0.15s;
          background: white;
        }
        .input-field:focus { border-color: #0d9488; box-shadow: 0 0 0 2px #ccfbf1; }
        select.input-field { cursor: pointer; }
      `}</style>
    </div>
  );
}
